'use client'

// ============================================================
// ConsentScriptLoader - Carrega scripts terceiros conforme consent
// ============================================================
// Le cookie ebuz_consent e injeta:
//   - Google Analytics 4 (GA4)   se analytics granted
//   - Meta Pixel                 se marketing granted
// Re-roda quando recebe evento "ebuz:consent-changed" do CookieBanner.
//
// Como adicionar um novo script terceiro:
//   1. Adicione o id no objeto SCRIPT_IDS
//   2. Crie uma funcao loadX() que injeta o script
//   3. Adicione no useEffect a chamada baseada na categoria
// ============================================================

import { useEffect } from 'react'

const COOKIE_NAME = 'ebuz_consent'

type ConsentCategory = 'essential' | 'analytics' | 'marketing' | 'personalization'
type ConsentMap = Record<ConsentCategory, boolean>

interface ConsentCookie {
  version: string
  categories: ConsentMap
  acceptedAt: string
}

const SCRIPT_IDS = {
  ga4: 'ebuz-script-ga4',
  metaPixel: 'ebuz-script-meta-pixel',
} as const

// ------------------------------------------------------------
// Helpers
// ------------------------------------------------------------
function readConsent(): ConsentCookie | null {
  if (typeof document === 'undefined') return null
  const match = document.cookie
    .split('; ')
    .find((row) => row.startsWith(`${COOKIE_NAME}=`))
  if (!match) return null
  try {
    return JSON.parse(decodeURIComponent(match.split('=')[1])) as ConsentCookie
  } catch {
    return null
  }
}

function ensureScript(id: string, src: string, attrs: Record<string, string> = {}) {
  if (document.getElementById(id)) return
  const s = document.createElement('script')
  s.id = id
  s.src = src
  s.async = true
  for (const [k, v] of Object.entries(attrs)) {
    s.setAttribute(k, v)
  }
  document.head.appendChild(s)
}

function ensureInlineScript(id: string, code: string) {
  if (document.getElementById(id)) return
  const s = document.createElement('script')
  s.id = id
  s.text = code
  document.head.appendChild(s)
}

function removeScript(id: string) {
  const el = document.getElementById(id)
  if (el) el.remove()
}

// ------------------------------------------------------------
// Loaders especificos
// ------------------------------------------------------------
function loadGA4(measurementId: string) {
  ensureScript(SCRIPT_IDS.ga4, `https://www.googletagmanager.com/gtag/js?id=${measurementId}`)
  ensureInlineScript(
    `${SCRIPT_IDS.ga4}-init`,
    `window.dataLayer = window.dataLayer || [];
     function gtag(){dataLayer.push(arguments);}
     gtag('js', new Date());
     gtag('config', '${measurementId}', { anonymize_ip: true });`
  )
}

function unloadGA4() {
  removeScript(SCRIPT_IDS.ga4)
  removeScript(`${SCRIPT_IDS.ga4}-init`)
  // GA tambem coloca cookies _ga*, mas sem dominio raiz nao da pra apagar do client
  // (cookie banner deve avisar usuario que precisa limpar manualmente).
}

function loadMetaPixel(pixelId: string) {
  ensureInlineScript(
    SCRIPT_IDS.metaPixel,
    `!function(f,b,e,v,n,t,s)
     {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
     n.callMethod.apply(n,arguments):n.queue.push(arguments)};
     if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
     n.queue=[];t=b.createElement(e);t.async=!0;
     t.src=v;s=b.getElementsByTagName(e)[0];
     s.parentNode.insertBefore(t,s)}(window, document,'script',
     'https://connect.facebook.net/en_US/fbevents.js');
     fbq('init', '${pixelId}');
     fbq('track', 'PageView');`
  )
}

function unloadMetaPixel() {
  removeScript(SCRIPT_IDS.metaPixel)
}

// ------------------------------------------------------------
// Componente
// ------------------------------------------------------------
interface ConsentScriptLoaderProps {
  ga4MeasurementId?: string
  metaPixelId?: string
}

export function ConsentScriptLoader({
  ga4MeasurementId,
  metaPixelId,
}: ConsentScriptLoaderProps) {
  useEffect(() => {
    function applyConsent() {
      const consent = readConsent()
      if (!consent) {
        // Sem consent: garante que nada esta carregado
        unloadGA4()
        unloadMetaPixel()
        return
      }

      // Analytics
      if (consent.categories.analytics && ga4MeasurementId) {
        loadGA4(ga4MeasurementId)
      } else {
        unloadGA4()
      }

      // Marketing
      if (consent.categories.marketing && metaPixelId) {
        loadMetaPixel(metaPixelId)
      } else {
        unloadMetaPixel()
      }
    }

    applyConsent()

    const handler = () => applyConsent()
    window.addEventListener('ebuz:consent-changed', handler)
    return () => {
      window.removeEventListener('ebuz:consent-changed', handler)
    }
  }, [ga4MeasurementId, metaPixelId])

  return null
}
